/**
 * Client-side image helper for icon uploads.
 *
 * Three input paths:
 *   1. Static image (PNG / JPG / WEBP) → canvas resize + JPEG/PNG
 *      re-encode, cap at ~150 KB binary
 *   2. Small GIF (≤ 200 KB) → passthrough as data: URL, animation
 *      preserved
 *   3. Big GIF (> 200 KB) → attempt client-side compression: decode
 *      frames with gifuct-js, render each onto a 128×128 (or 96×96)
 *      canvas, re-encode with gifenc. Two escalations: first try at
 *      128×128 full framerate, then 128×128 every-other-frame, then
 *      96×96 every-other-frame. If still over the limit, reject with
 *      a clear message.
 *
 * Server iconUrl cap is 300 000 chars; base64 expands ~4/3 so binary
 * ceiling is ~225 KB. We aim 200 KB to leave header overhead.
 *
 * When S3 attachments ship this whole module collapses to "upload
 * blob, return URL" and size caps move to ffmpeg-side reencoding.
 */

import i18n from '@/i18n';
import { parseGIF, decompressFrames, type ParsedFrame } from 'gifuct-js';
import { GIFEncoder, quantize, applyPalette } from 'gifenc';

const MAX_INPUT_BYTES = 4 * 1024 * 1024; // 4 MB
const TARGET_SIDE = 256;
const TARGET_BYTES = 150 * 1024;
const GIF_MAX_BYTES = 200 * 1024;

/**
 * cispracs rejects animated GIFs above 256 px on the longest side rather than
 * downscaling them (their §3 limits table) — Go's `gif.EncodeAll` needs
 * paletted frames, so resizing would mean re-quantising every frame.
 */
const GIF_MAX_SIDE = 256;

/** Public so the crop modal renders to the same square the auto path uses. */
export const ICON_TARGET_SIDE = TARGET_SIDE;
export const ICON_MAX_INPUT_BYTES = MAX_INPUT_BYTES;

/**
 * Encode an already-cropped square canvas to a size-capped data URL — the
 * shared tail of both the auto-crop path and the manual crop modal. JPEG with
 * a quality ladder for opaque images; PNG (then one downscale) when alpha
 * matters.
 */
export async function encodeIconCanvas(
  canvas: HTMLCanvasElement,
  hasAlpha: boolean,
): Promise<string> {
  // Guards the crop modal, which draws its own canvas and calls straight in here.
  assertCanvasPainted(canvas);
  if (hasAlpha) {
    const png = await canvasToDataUrl(canvas, 'image/png');
    if (dataUrlBytes(png) <= TARGET_BYTES) return png;
    return canvasToDataUrl(downscaleCanvas(canvas, Math.round(canvas.width * 0.75)), 'image/png');
  }
  for (const q of [0.85, 0.78, 0.7, 0.6]) {
    const dataUrl = await canvasToDataUrl(canvas, 'image/jpeg', q);
    if (dataUrlBytes(dataUrl) <= TARGET_BYTES) return dataUrl;
  }
  return canvasToDataUrl(canvas, 'image/jpeg', 0.5);
}

function downscaleCanvas(src: HTMLCanvasElement, side: number): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = side;
  out.height = side;
  const ctx = out.getContext('2d');
  if (!ctx) throw new ImageUploadError('invalid', i18n.t('lib2.image.noCanvas'));
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, side, side);
  return out;
}

export class ImageUploadError extends Error {
  constructor(public code: 'too_large' | 'invalid' | 'unsupported', message: string) {
    super(message);
    this.name = 'ImageUploadError';
  }
}

export async function fileToIconDataUrl(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new ImageUploadError('unsupported', i18n.t('lib2.image.notImage'));
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new ImageUploadError(
      'too_large',
      i18n.t('lib2.image.fileTooLarge', { size: (file.size / 1024 / 1024).toFixed(1) }),
    );
  }

  // GIF — preserve animation. Pass through if it already fits, otherwise
  // try progressive compression before giving up.
  if (file.type === 'image/gif') {
    // Dimension gate FIRST, before the size branch. This is the hole: a small
    // but large-dimensioned GIF (300x300 at 150 KB) took the passthrough below
    // without ever being decoded, sailed past every local check, and was
    // rejected by cispracs at 256 px — an error the user could do nothing
    // with. Reading the Logical Screen Descriptor is the only way to know:
    // file.size says nothing about dimensions.
    await assertGifWithinDimensions(file);
    if (file.size <= GIF_MAX_BYTES) {
      return fileToBase64DataUrl(file);
    }
    return shrinkGifProgressive(file);
  }

  const bitmap = await loadBitmap(file);
  const { canvas, hasAlpha } = drawResized(bitmap, file.type);
  // Before encoding, not after: an unpainted canvas produces a valid black
  // JPEG that nothing downstream can distinguish from a real image.
  assertCanvasPainted(canvas);

  // Try JPEG first unless we have alpha; PNG keeps transparency at the cost
  // of size. If PNG comes out over the target, try once more after another
  // downscale; we still keep it under the cap by being conservative.
  if (hasAlpha) {
    const png = await canvasToDataUrl(canvas, 'image/png');
    if (dataUrlBytes(png) <= TARGET_BYTES) return png;
    const smaller = redraw(bitmap, Math.round(TARGET_SIDE * 0.75));
    return canvasToDataUrl(smaller, 'image/png');
  }

  for (const q of [0.85, 0.78, 0.7, 0.6]) {
    const dataUrl = await canvasToDataUrl(canvas, 'image/jpeg', q);
    if (dataUrlBytes(dataUrl) <= TARGET_BYTES) return dataUrl;
  }
  return canvasToDataUrl(canvas, 'image/jpeg', 0.5);
}

// ---------------------------------------------------------------------------
// GIF compression
// ---------------------------------------------------------------------------

/**
 * Try to bring a too-big GIF under the cap by re-encoding at smaller
 * resolution and/or dropping every other frame. Three escalations,
 * cheapest first; each rebuilds the GIF from scratch which is expensive
 * (decode + per-frame quantise + encode) but typical icon GIFs have
 * 20-50 frames at 200-400 px, so worst-case is ~500 ms on a modern CPU.
 *
 * Strategy:
 *   1. 128×128, every frame
 *   2. 128×128, every-other frame
 *   3. 96×96,  every-other frame
 *
 * If all three still exceed the cap, throw with a message that
 * acknowledges we already tried.
 */
async function shrinkGifProgressive(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  let parsed;
  let frames: ParsedFrame[];
  try {
    parsed = parseGIF(buf);
    frames = decompressFrames(parsed, true);
  } catch {
    throw new ImageUploadError('invalid', i18n.t('lib2.image.gifUnreadable'));
  }
  if (!frames.length) {
    throw new ImageUploadError('invalid', i18n.t('lib2.image.gifNoFrames'));
  }

  const sourceWidth = parsed.lsd.width;
  const sourceHeight = parsed.lsd.height;

  const attempts: Array<{ size: number; everyOther: boolean }> = [
    { size: 128, everyOther: false },
    { size: 128, everyOther: true },
    { size: 96, everyOther: true },
  ];

  for (const a of attempts) {
    const bytes = await encodeShrunkGif(frames, sourceWidth, sourceHeight, a.size, a.everyOther);
    if (bytes.length <= GIF_MAX_BYTES) {
      return `data:image/gif;base64,${bytesToBase64(bytes)}`;
    }
  }

  throw new ImageUploadError(
    'too_large',
    i18n.t('lib2.image.gifTooBig', { size: (file.size / 1024).toFixed(0) }),
  );
}

/**
 * Re-encode the GIF at target side length. Composites each frame onto a
 * canvas matching the source GIF size (respecting disposal type), then
 * scales + center-crops to the target square and emits one GIF frame.
 */
async function encodeShrunkGif(
  frames: ParsedFrame[],
  sourceWidth: number,
  sourceHeight: number,
  targetSide: number,
  everyOther: boolean,
): Promise<Uint8Array> {
  // Source canvas holds the running composite — each GIF frame paints
  // onto it as a patch at frame.dims, like a real GIF renderer.
  const srcCanvas = document.createElement('canvas');
  srcCanvas.width = sourceWidth;
  srcCanvas.height = sourceHeight;
  const srcCtx = srcCanvas.getContext('2d');
  if (!srcCtx) throw new ImageUploadError('invalid', i18n.t('lib2.image.noCanvas'));

  // Destination canvas is what the GIF encoder reads pixels from.
  const dstCanvas = document.createElement('canvas');
  dstCanvas.width = targetSide;
  dstCanvas.height = targetSide;
  const dstCtx = dstCanvas.getContext('2d');
  if (!dstCtx) throw new ImageUploadError('invalid', i18n.t('lib2.image.noCanvas'));
  dstCtx.imageSmoothingEnabled = true;
  dstCtx.imageSmoothingQuality = 'high';

  const encoder = GIFEncoder();

  // Center-crop math is the same for every frame — precompute.
  const srcSide = Math.min(sourceWidth, sourceHeight);
  const sx = (sourceWidth - srcSide) / 2;
  const sy = (sourceHeight - srcSide) / 2;

  let accumulatedDelayMs = 0;
  let anyFramePainted = false;
  let emitted = 0;

  for (let i = 0; i < frames.length; i++) {
    const frame = frames[i];
    if (!frame) continue;

    // Disposal type 2 = clear to background before drawing this frame.
    // (Types 0, 1 = leave previous; 3 = restore-previous, rare, skipped.)
    if (frame.disposalType === 2) {
      srcCtx.clearRect(
        frame.dims.left,
        frame.dims.top,
        frame.dims.width,
        frame.dims.height,
      );
    }

    // Composite the frame's patch onto the source canvas at its position.
    const patchData = new ImageData(
      new Uint8ClampedArray(frame.patch),
      frame.dims.width,
      frame.dims.height,
    );
    const patchCanvas = document.createElement('canvas');
    patchCanvas.width = frame.dims.width;
    patchCanvas.height = frame.dims.height;
    patchCanvas.getContext('2d')!.putImageData(patchData, 0, 0);
    srcCtx.drawImage(patchCanvas, frame.dims.left, frame.dims.top);

    // Accumulate delay so dropped frames don't shorten the loop.
    accumulatedDelayMs += frame.delay;

    if (everyOther && i % 2 === 1) continue;

    // Scale + center-crop to the target square.
    dstCtx.clearRect(0, 0, targetSide, targetSide);
    dstCtx.drawImage(srcCanvas, sx, sy, srcSide, srcSide, 0, 0, targetSide, targetSide);
    const pixels = dstCtx.getImageData(0, 0, targetSide, targetSide).data;

    // Same blank-canvas failure as the still-image path, through a different
    // door: this branch returns before `fileToIconDataUrl`'s guard, and it
    // draws twice (composite above, scale here). On a machine where drawImage
    // is a no-op every composed frame reads back transparent and a GIF of blank
    // frames ships into cispracs' no-delete store.
    //
    // Reuses the readback already taken for quantisation — no extra
    // getImageData. Early-exits once anything is painted, so the scan costs
    // nothing after the first non-empty frame.
    if (!anyFramePainted) {
      for (let a = 3; a < pixels.length; a += 4) {
        if (pixels[a] !== 0) { anyFramePainted = true; break; }
      }
    }

    // Quantise to ≤256 colours per frame. Single global palette would be
    // smaller but degrades quality noticeably for icons that change scenes.
    const palette = quantize(pixels, 256);
    const indexed = applyPalette(pixels, palette);

    encoder.writeFrame(indexed, targetSide, targetSide, {
      palette,
      delay: Math.max(20, accumulatedDelayMs),
      // First-frame flag triggers logical-screen-descriptor write inside
      // gifenc; we use the auto mode so this comes through implicitly.
    });
    accumulatedDelayMs = 0;
    emitted++;
  }

  if (emitted === 0) {
    throw new ImageUploadError('invalid', i18n.t('lib2.image.noFramesLeft'));
  }
  // Rejected only if EVERY composed frame was empty. A legitimately blank
  // first frame is possible with GIF disposal tricks, so per-frame rejection
  // would be a false positive; "no frame had a single painted pixel" is not.
  if (!anyFramePainted) {
    throw new ImageUploadError('invalid', i18n.t('lib2.image.emptyCanvas'));
  }

  encoder.finish();
  return encoder.bytes();
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function fileToBase64DataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new ImageUploadError('invalid', i18n.t('lib2.image.fileUnreadable')));
        return;
      }
      resolve(result);
    };
    reader.onerror = () =>
      reject(new ImageUploadError('invalid', i18n.t('lib2.image.fileUnreadable')));
    reader.readAsDataURL(file);
  });
}

function bytesToBase64(bytes: Uint8Array): string {
  // Chunked to dodge "Maximum call stack size exceeded" with String.fromCharCode
  // on really big buffers. 8192 is comfortable for V8.
  const CHUNK = 8192;
  let bin = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

function loadBitmap(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new ImageUploadError('invalid', i18n.t('lib2.image.imageUnreadable')));
    };
    img.src = url;
  });
}

function drawResized(
  img: HTMLImageElement,
  mime: string,
): { canvas: HTMLCanvasElement; hasAlpha: boolean } {
  const side = Math.min(TARGET_SIDE, Math.max(img.width, img.height));
  const canvas = redraw(img, side);
  return {
    canvas,
    hasAlpha: mime === 'image/png' || mime === 'image/webp',
  };
}

function redraw(img: HTMLImageElement, side: number): HTMLCanvasElement {
  const sourceSide = Math.min(img.width, img.height);
  const sx = (img.width - sourceSide) / 2;
  const sy = (img.height - sourceSide) / 2;

  const canvas = document.createElement('canvas');
  canvas.width = side;
  canvas.height = side;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new ImageUploadError('invalid', i18n.t('lib2.image.noCanvas'));
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, sourceSide, sourceSide, 0, 0, side, side);
  return canvas;
}

function canvasToDataUrl(
  canvas: HTMLCanvasElement,
  mime: 'image/jpeg' | 'image/png',
  quality?: number,
): Promise<string> {
  return Promise.resolve(canvas.toDataURL(mime, quality));
}

function dataUrlBytes(dataUrl: string): number {
  const i = dataUrl.indexOf(',');
  const b64 = i < 0 ? dataUrl : dataUrl.slice(i + 1);
  return Math.floor((b64.length * 3) / 4);
}

/**
 * Reject a GIF whose logical screen exceeds what cispracs will store.
 *
 * `parseGIF` reads the Logical Screen Descriptor — the animation's own
 * dimensions, which is what their limit applies to. Not `<img>.naturalWidth`:
 * that reports the first frame, and a GIF's frames may be smaller than the
 * logical screen they compose onto.
 */
export async function assertGifWithinDimensions(file: File): Promise<void> {
  const invalid = () =>
    new ImageUploadError('invalid', i18n.t('lib2.image.gifTryAnother'));

  const buf = await file.arrayBuffer();

  // Check the signature ourselves first. `parseGIF` does NOT reliably throw on
  // junk — handed four random bytes it returns an lsd built from data that
  // isn't there, so a malformed file would sail through this gate with
  // plausible-looking dimensions. Found by a test that expected a throw and
  // got a pass.
  const sig = new TextDecoder('latin1').decode(new Uint8Array(buf, 0, Math.min(6, buf.byteLength)));
  if (sig !== 'GIF87a' && sig !== 'GIF89a') throw invalid();

  let lsd: { width: number; height: number };
  try {
    lsd = parseGIF(buf).lsd;
  } catch {
    // A GIF we cannot parse is one cispracs cannot decode either.
    throw invalid();
  }
  if (!Number.isFinite(lsd.width) || !Number.isFinite(lsd.height) || lsd.width < 1 || lsd.height < 1) {
    throw invalid();
  }
  if (lsd.width > GIF_MAX_SIDE || lsd.height > GIF_MAX_SIDE) {
    throw new ImageUploadError(
      'too_large',
      i18n.t('lib2.image.animationTooLarge', { width: lsd.width, height: lsd.height, max: GIF_MAX_SIDE }),
    );
  }
}

/**
 * Turn a `data:` URL produced above back into bytes for a multipart upload.
 *
 * Decoded in-process. It must NOT use `fetch()`: a `data:` URL is a string we
 * already hold, but `fetch` is governed by CSP `connect-src`, and this app's
 * policy (`client/index.html`) allows `data:` under `img-src` only. So
 * `fetch(dataUrl)` is refused by the browser with a `TypeError` — which, not
 * being an `ImageUploadError`, surfaced to the user as the generic
 * "не удалось обработать изображение" and broke avatar upload for EVERY
 * format, not just GIF. Shipped in e5e7727c, found in prod.
 *
 * jsdom does not enforce CSP, so no unit test could have caught the original —
 * which is why `avatarUpload.test.ts` pins the absence of `fetch` directly
 * rather than only checking the output.
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(',');
  if (!dataUrl.startsWith('data:') || comma === -1) {
    throw new ImageUploadError('invalid', i18n.t('lib2.image.imageUnreadable'));
  }
  const meta = dataUrl.slice(5, comma); // between "data:" and ","
  const payload = dataUrl.slice(comma + 1);
  const isBase64 = /;base64$/i.test(meta);
  const mime = meta.replace(/;base64$/i, '') || 'application/octet-stream';

  // `fileToIconDataUrl` always emits base64, but honour the un-encoded form too
  // rather than silently mis-decoding a URL that is legal per the data: spec.
  if (!isBase64) {
    return new Blob([decodeURIComponent(payload)], { type: mime });
  }
  const bin = atob(payload);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/**
 * Refuse to encode a canvas that nothing was drawn onto.
 *
 * WHY THIS EXISTS (prod defect, 2026-08-27): on a browser where
 * `ctx.drawImage()` is a silent no-op — seen on Firefox/Linux with a broken
 * WebRender path — the canvas is left fully transparent. `toDataURL('image/jpeg')`
 * of a transparent canvas is a **valid, correctly sized, solid black JPEG**,
 * because JPEG has no alpha channel and transparent pixels carry RGB (0,0,0).
 *
 * That is what makes the failure invisible: every check downstream passes —
 * magic bytes, size cap, format allowlist, cispracs' own decoder — because the
 * bytes genuinely are a valid JPEG. The user sees success and a black square is
 * written to permanent storage, and for avatars into cispracs' store, where we
 * have no delete operation.
 *
 * FULL SCAN, NOT SAMPLED. The defect report suggested five sample points. That
 * detects this failure, but rejects a legitimate image that happens to be
 * transparent at all five — a ring or wide-border logo — and blames the user's
 * graphics driver for it. Scanning every pixel costs well under a millisecond
 * on a 256px canvas for a one-off user action, and makes a false positive
 * impossible: any single painted pixel passes.
 *
 * `getImageData` is trustworthy in exactly this failure mode — the same report
 * verified that `fillRect` + readback are correct on the affected machine, and
 * that the canvas reads back as transparent after the failed `drawImage`.
 */
export function assertCanvasPainted(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ImageUploadError('invalid', i18n.t('lib2.image.noCanvas'));

  const { width, height } = canvas;
  if (width === 0 || height === 0) throw new ImageUploadError('invalid', i18n.t('lib2.image.emptyCanvas'));

  const { data } = ctx.getImageData(0, 0, width, height);
  // Alpha only. A legitimately all-black image must still upload, so "every
  // pixel is RGB(0,0,0)" is the wrong test — an opaque black image has
  // alpha 255 everywhere, which passes here as it should.
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] !== 0) return;
  }
  throw new ImageUploadError('invalid', i18n.t('lib2.image.emptyCanvas'));
}
